import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { filter, take } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { InterventionInstanceStore } from '../../../store/intervention-instance/component-store/intervention-instance.store';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { UserInterface } from 'src/app/models/interface/user.interface';
import { InterventionInterface } from 'src/app/models/interface/intervention.interface';
import { InterventionInstanceInterface } from 'src/app/models/interface/intervention-instances/intervention-instance.interface';

export type DialogInterventionInstanceDeleteDataParam = {
  intervention_name: string;
};
export type DialogInterventionInstanceDeleteData = {
  param: DialogInterventionInstanceDeleteDataParam;
  collaborators: UserInterface[];
  intervention: InterventionInterface;
  members: UserInterface[];
  isManager: boolean;
  instance: InterventionInstanceInterface;
};

export type DialogInterventionInstanceDeleteResult = undefined | 'CANCELED' | 'SUCCESS';

type ResponseData = 'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE';

@Component({
  selector: 'app-dialog-intervention-instance-delete',
  templateUrl: './dialog-intervention-instance-delete.component.html',
  styleUrls: ['./dialog-intervention-instance-delete.component.scss'],
  providers: [InterventionInstanceStore],
})
export class DialogInterventionInstanceDeleteComponent implements OnInit, OnDestroy {

  isManager: boolean;

  intervention: InterventionInterface;
  instance: InterventionInstanceInterface;
  collaborators: UserInterface[];
  members: UserInterface[];

  acceptDeletion = false;

  buddyError = false;

  public setDeletionResponse = new BehaviorSubject<ResponseData>('DEFAULT');

  public param: DialogInterventionInstanceDeleteDataParam;
  public buddyParam = { buddies: '' };

  private deleteInstanceResponse$: Observable<any>;

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogInterventionInstanceDeleteData,
    private interventionInstanceStore: InterventionInstanceStore,
    private dialogRef: MatDialogRef<DialogInterventionInstanceDeleteComponent, DialogInterventionInstanceDeleteResult>,
  ) {
    this.deleteInstanceResponse$ = this.interventionInstanceStore.deleteInstanceResponse$;
  }

  ngOnInit(): void {
    this.param = this.data.param;
    this.isManager = this.data.isManager;
    this.intervention = this.data.intervention;
    this.instance = this.data.instance;
    this.collaborators = this.data.collaborators;
    this.members = this.data.members;
  }

  changeAcceptDeletion(acceptDeletion: boolean) {
    this.acceptDeletion = acceptDeletion;
  }

  isDisabled(): boolean {
    return !this.acceptDeletion || this.setDeletionResponse.value !== 'DEFAULT' || !this.isManager;
  }

  public deleteInstance(): void {
    if (this.isDisabled()) {
      return;
    }
    this.setDeletionResponse.next('LOADING');
    this.interventionInstanceStore.deleteInstanceAsEM({
      instanceId: this.instance.id,
    });
    this.subscriptions.push(
      this.deleteInstanceResponse$
        .pipe(
          filter((value) => !!value),
          take(1),
        )
        .subscribe((value) => {
          if (value instanceof HttpResponse) {
            this.setDeletionResponse.next('SUCCESS');
            setTimeout(() => {
              this.dialogRef.close('SUCCESS');
            }, 2500);
          } else {
            this.setDeletionResponse.next('FAILURE');
            if (value instanceof HttpErrorResponse) {
              const error = value.error?.errors?.at(0);
              if (error?.code?.toString() === '1516') {
                // Example for detail: "The intervention instance is part of the following buddies: 87"
                this.buddyParam.buddies = error.detail?.split(':').at(1)?.trim() ?? '';
                this.buddyError = true;
                return;
              }
            }
            setTimeout(() => {
              this.setDeletionResponse.next('DEFAULT');
            }, 2500);
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
